// app/src/features/dev/DebugHost.jsx
import React from "react";
import { DebugPanel } from "../../components/Debug.jsx";
import { C } from "../../lib/colors.js";
import { surface, ui } from "../../lib/telemetry/instrument.js";

/**
 * The pipeline-debug strip (the `pipeline-debug` surface with its `pipeline-debug.toggle` control
 * and, while open, `components/Debug.jsx`'s `DebugPanel`), rendered inside `AppFrame`'s `overlays`
 * slot (desktop-only — the frame renders the slot on its desktop branch alone) exactly where the
 * inline strip sat: after the selection-rectangle box, before the signal library overlay. Named
 * `DebugHost`, not the plan's `DebugPanel.jsx`: that name is `components/Debug.jsx`'s export, which
 * this host renders and the telemetry registry names.
 *
 * `ctx` is the frame context: the settings' `debugUI` gate, `forecastEngine` and `compareEngine`,
 * the focused forecast (`active`), the timeline's compare state (`compareOn`, `compareRoots`) and
 * the strip's own open flag with its raw setter (`debugPanelOpen`, `setDebugPanelOpen`). The host
 * hands `ctx` nowhere.
 */
export default function DebugHost({ ctx }) {
  const {
    debugUI,
    active,
    compareOn,
    compareRoots,
    forecastEngine,
    compareEngine,
    debugPanelOpen,
    setDebugPanelOpen,
  } = ctx;
  return (
    <>
      {debugUI && (active?._debug || compareRoots[1]) && (
        <div
          {...surface("pipeline-debug")}
          style={{
            borderTop: "2px solid #33aa88",
            background: C.debugPanelBg,
            maxHeight: debugPanelOpen ? 500 : 28,
            overflow: "hidden",
            transition: "max-height 0.3s ease",
            flexShrink: 0,
          }}
        >
          <div
            {...ui("pipeline-debug.toggle")}
            onClick={() => setDebugPanelOpen((v) => !v)}
            style={{
              padding: "5px 14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: "#33aa88",
                letterSpacing: 0.6,
                fontWeight: "bold",
              }}
            >
              PIPELINE DEBUG
            </span>
            <span style={{ fontSize: 12, color: "#33aa88" }}>
              {debugPanelOpen ? "\u25BC" : "\u25B2"}
            </span>
            {active?._debug && (
              <span style={{ fontSize: 11, color: "#556" }}>
                Events: {active?.events?.length || 0}
                {compareOn && compareRoots[1]
                  ? ` · vs ${compareRoots[1]?.events?.length || 0}`
                  : ""}
              </span>
            )}
          </div>
          {(() => {
            const cmp = (compareOn && compareRoots[1]) || null;
            return (
              debugPanelOpen && (
                <DebugPanel
                  primary={active}
                  comparison={cmp}
                  primaryEngine={forecastEngine}
                  compareEngine={
                    cmp?._engine ||
                    compareEngine ||
                    (forecastEngine === "llm" ? "research_partial" : "llm")
                  }
                />
              )
            );
          })()}
        </div>
      )}
    </>
  );
}
